import { useState, useMemo } from 'react'
import { icons } from 'lucide-react'
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover'
import { RECOMMENDED_ICONS, ALL_TAB_ICONS } from './tab-icon-list'

interface IconPickerPopoverProps {
  currentIcon: string
  onSelect: (iconName: string) => void
}

export function IconPickerPopover({ currentIcon, onSelect }: IconPickerPopoverProps) {
  const [open, setOpen] = useState(false)
  const [search, setSearch] = useState('')

  const CurrentIcon = icons[currentIcon as keyof typeof icons] ?? icons.CircleAlert

  const filteredIcons = useMemo(() => {
    if (!search.trim()) return []
    const query = search.toLowerCase()
    return ALL_TAB_ICONS.filter(
      (name) =>
        name.toLowerCase().includes(query) &&
        !RECOMMENDED_ICONS.includes(name as (typeof RECOMMENDED_ICONS)[number])
    )
  }, [search])

  function handleSelect(name: string) {
    onSelect(name)
    setOpen(false)
    setSearch('')
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className="flex items-center gap-2 rounded-sm border border-gray-200 px-3 py-1.5 text-[13px] transition-colors hover:bg-gray-50"
        >
          <CurrentIcon className="size-5 text-gray-600" strokeWidth={2} />
          <span className="text-blue-600">เปลี่ยน</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        sideOffset={4}
        className="w-[280px] p-3"
      >
        {/* Recommended icons */}
        <div className="mb-2 text-[11px] font-semibold text-gray-500">แนะนำ</div>
        <div className="grid grid-cols-4 gap-1">
          {RECOMMENDED_ICONS.map((name) => {
            const Icon = icons[name as keyof typeof icons]
            if (!Icon) return null
            return (
              <button
                key={name}
                type="button"
                onClick={() => handleSelect(name)}
                className={`flex h-11 w-full items-center justify-center rounded-sm transition-colors ${
                  currentIcon === name
                    ? 'bg-blue-50 text-blue-600'
                    : 'text-gray-600 hover:bg-gray-100'
                }`}
                title={name}
              >
                <Icon className="size-5" strokeWidth={2} />
              </button>
            )
          })}
        </div>

        {/* Search input */}
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="ค้นหาไอคอน..."
          className="mt-2 w-full h-8 rounded-sm border border-gray-200 px-2.5 text-[12px] outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
        />

        {/* Search results */}
        {search.trim() && (
          <div className="mt-2 max-h-[200px] overflow-y-auto">
            {filteredIcons.length === 0 ? (
              <p className="py-4 text-center text-[12px] text-gray-400">
                ไม่พบไอคอน
              </p>
            ) : (
              <div className="grid grid-cols-4 gap-1">
                {filteredIcons.map((name) => {
                  const Icon = icons[name as keyof typeof icons]
                  if (!Icon) return null
                  return (
                    <button
                      key={name}
                      type="button"
                      onClick={() => handleSelect(name)}
                      className={`flex h-11 w-full items-center justify-center rounded-sm transition-colors ${
                        currentIcon === name
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                      title={name}
                    >
                      <Icon className="size-5" strokeWidth={2} />
                    </button>
                  )
                })}
              </div>
            )}
          </div>
        )}
      </PopoverContent>
    </Popover>
  )
}
